import React, { useState, useEffect, useRef, useCallback } from "react";
import { Form, Col, Image, Row, Card, Button, Modal } from "react-bootstrap";
import { PageSpinner } from "@cdk-uip/react-page-spinner";
import { PDFViewer } from "@react-pdf/renderer";
import { toast } from "react-toastify";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { PDFTemplate } from "./PDFTemplate";
import Dialog from "../common/Dialog";
import ErrorDialog from "../common/ErrorDialog";
import SubmitSuccess from "./SubmitSuccess";
import {
  DECLARATION_PLACES,
  alphaNumericWithSpecialCharacterPattern,
  cropConfig,
  defaultToastOptions,
  embeddedSpacesPattern,
  positiveNumberPattern
} from "../../constants/constants";
import { imageCompressor } from "../../util/helpers";
import api from "../../api/EmployeeExit.resource";
import useValidation from "../../util/useValidation";
import "./styles.css";

const LeaveAndGratuityForm = () => {
  const imgRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const taxProofSubmission = useRef(null);
  const [crop, setCrop] = useState(cropConfig);
  const [croppedImage, setCroppedImage] = useState(null);
  const [employeeDetails, setEmployeeDetails] = useState({
    employeeDateOfLeaving: "2021-09-30"
  });
  const [errorAlert, setErrorAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [imageErrors, setImageErrors] = useState("");
  const [isValid, setIsValid] = useState(false);
  const [pdfType, setPdfType] = useState("Leave Encashment");
  const [showImage, setShowImage] = useState(false);
  const [showPdf, setShowPdf] = useState(false);
  const [submitAlert, setSubmitAlert] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitScreen, setSubmitScreen] = useState(false);

  const { handleOnBlurValidation, errors } = useValidation();
  const handleOnBlurInput = event => handleOnBlurValidation(event);
  const onLoad = useCallback(img => {
    imgRef.current = img;
  }, []);

  useEffect(() => {
    setCrop(cropConfig);
  }, [employeeDetails.employeeSignature]);

  useEffect(() => {
    setIsValid(isValidForm());
  });

  function isValidForm() {
    return (
      taxProofSubmission.current &&
      taxProofSubmission.current.checkValidity() &&
      !imageErrors
    );
  }

  if (submitScreen) {
    return <SubmitSuccess />;
  }

  const onCompleteCrop = crop => {
    if (!crop || !previewCanvasRef.current || !imgRef.current) {
      return;
    }

    const image = imgRef.current;
    const canvas = previewCanvasRef.current;

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const ctx = canvas.getContext("2d");
    const pixelRatio = window.devicePixelRatio;

    canvas.width = crop.width * pixelRatio * scaleX;
    canvas.height = crop.height * pixelRatio * scaleY;

    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = "high";

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width * scaleX,
      crop.height * scaleY
    );
  };

  const showCroppedImage = async () => {
    setShowImage(false);
    try {
      const croppedImage = await canvasToFile(previewCanvasRef.current);
      setEmployeeDetails({
        ...employeeDetails,
        employeeSignature: croppedImage
      });
      setCroppedImage(croppedImage);
    } catch (e) {
      console.error(e);
    }
  };

  const onFileChange = async e => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.addEventListener("load", () =>
        setEmployeeDetails({
          ...employeeDetails,
          employeeSignature: reader.result
        })
      );
      reader.readAsDataURL(e.target.files[0]);
      const fileError = isFileValid(file);
      if (fileError) {
        setImageErrors(fileError);
        return;
      } else setImageErrors(false);

      setShowImage(true);
    }
  };

  const canvasToFile = async canvas => {
    const res = await fetch(canvas.toDataURL("image/jpeg"));
    const blob = await res.blob();
    return new File([blob], `signature.png`, { type: "image/png" });
  };
  const handleOnChange = ({ target: { name, value } }) => {
    if (embeddedSpacesPattern.test(value)) {
      setEmployeeDetails({ ...employeeDetails, [name]: value.trimStart() });
    }
  };

  const isFileValid = file => {
    let msg = null;
    if (file.size > 2097152) msg = "Image size must be less than 2 MB";
    if (!["image/jpeg", "image/jpg", "image/png"].includes(file.type))
      msg = msg
        ? "Image must be jpeg, jpg, png format and less than 2 MB"
        : "Image must be jpeg, jpg, png format";
    return msg;
  };

  const onFileUploadClick = e => {
    e.target.value = null;
    setCroppedImage(null);
    setEmployeeDetails({
      ...employeeDetails,
      employeeSignature: null
    });
  };

  const handleClosePdf = () => setShowPdf(false);

  const generatePdf = (type = "Leave Encashment") => {
    setShowPdf(true);
    setPdfType(type);
  };

  const saveEmployeeFormDetails = async () => {
    setSubmitAlert(false);
    setSubmitLoading(true);
    try {
      const formData = new FormData(taxProofSubmission.current);
      formData.append("status", "1");
      const employeeSignatureCompressedImage = await imageCompressor(
        croppedImage
      );
      formData.set("employeeSignature", employeeSignatureCompressedImage);
      const addEmployee = await api.addEmployee(formData);
      if (addEmployee && !addEmployee.errorMessage) {
        setSubmitScreen(true);
      } else {
        if (
          addEmployee.errorMessage ===
          "Employee details already exist in the system"
        )
          setErrorMessage(
            "Details with this Employee Id already exists. Please contact HR if any changes required."
          );
        else setErrorMessage("Error while Uploading Details");
        setErrorAlert(true);
      }
    } catch (error) {
      toast.error(`Error while Uploading Details`, defaultToastOptions);
    } finally {
      setSubmitLoading(false);
    }
  };

  const onImageClose = () => {
    fileInputRef.current.value = null;
    setCroppedImage(null);
    setShowImage(false);
    setEmployeeDetails({
      ...employeeDetails,
      employeeSignature: null
    });
  };
  return (
    <Form className="w-60 pt-3 m-auto" noValidate ref={taxProofSubmission}>
      <div className="container w-100 mx-auto org-details approval-form">
        <Card>
          <Card.Header className="accordian-header text-left">
            <Row>
              <Col sm={12} className="text-center">
                Leave Encashment and Gratuity Declaration Form
              </Col>
            </Row>
          </Card.Header>
          <Card.Body>
            <Form.Group as={Row} controlId="cdkEmployeeId">
              <Form.Label column sm={4} className="text-right">
                Employee ID*
              </Form.Label>
              <Col sm={6}>
                <Form.Control
                  name="cdkEmployeeId"
                  isInvalid={errors.cdkEmployeeId}
                  onBlur={handleOnBlurInput}
                  onChange={handleOnChange}
                  pattern={positiveNumberPattern}
                  placeholder="Eg: 123456"
                  required
                  size="sm"
                  value={employeeDetails.cdkEmployeeId}
                />
                {errors.cdkEmployeeId && (
                  <Form.Control.Feedback type="invalid">
                    {errors.cdkEmployeeId.type === "required" &&
                      "Employee ID is required"}
                    {errors.cdkEmployeeId.type === "pattern" &&
                      "Employee ID should be a numeric value"}
                  </Form.Control.Feedback>
                )}
              </Col>
            </Form.Group>
            <Form.Group as={Row}>
              <Form.Label column sm={4} className="text-right">
                Employee Name*
              </Form.Label>
              <Col sm={6}>
                <Form.Control
                  name="cdkEmployeeName"
                  isInvalid={errors.cdkEmployeeName}
                  onBlur={handleOnBlurInput}
                  onChange={handleOnChange}
                  placeholder="Full Name"
                  required
                  size="sm"
                  type="text"
                  value={employeeDetails.cdkEmployeeName}
                  pattern={alphaNumericWithSpecialCharacterPattern}
                />
                {errors.cdkEmployeeName && (
                  <Form.Control.Feedback type="invalid">
                    {errors.cdkEmployeeName.type === "required" &&
                      "Name of the Employee is required"}
                    {errors.cdkEmployeeName.type === "pattern" &&
                      "Invalid characters entered for Name"}
                  </Form.Control.Feedback>
                )}
              </Col>
            </Form.Group>
            <Form.Group as={Row} controlId="employeePlace">
              <Form.Label column sm={4} className="text-right">
                Place*
              </Form.Label>

              <Col sm={6}>
                <Form.Control
                  name="employeePlace"
                  className="w-50"
                  isInvalid={errors.employeePlace}
                  onBlur={handleOnBlurInput}
                  size="sm"
                  required
                  as="select"
                  value={employeeDetails.employeePlace}
                  onChange={handleOnChange}
                >
                  <option value="">Choose...</option>
                  {DECLARATION_PLACES.map(place => (
                    <option value={place.id}>{place.name}</option>
                  ))}
                </Form.Control>
                {errors.employeePlace && (
                  <Form.Control.Feedback type="invalid">
                    {errors.employeePlace.type === "required" &&
                      "Place is required"}
                  </Form.Control.Feedback>
                )}
              </Col>
            </Form.Group>

            <Form.Group as={Row} controlId="employeeDateOfLeaving">
              <Form.Label column sm={4} className="text-right">
                Date of Leaving*
              </Form.Label>
              <Col sm={6}>
                <Form.Control
                  className="w-50"
                  name="employeeDateOfLeaving"
                  onKeyDown={() => false}
                  tabIndex="0"
                  isInvalid={errors.employeeDateOfLeaving}
                  onBlur={handleOnBlurInput}
                  onChange={handleOnChange}
                  required
                  size="sm"
                  type="date"
                  value={employeeDetails.employeeDateOfLeaving}
                />
                {errors.employeeDateOfLeaving && (
                  <Form.Control.Feedback type="invalid">
                    {errors.employeeDateOfLeaving.type === "required" &&
                      "Date of Leaving is required"}
                  </Form.Control.Feedback>
                )}
              </Col>
            </Form.Group>
            <Form.Group as={Row}>
              <Form.Label column sm={4} className="text-right">
                Employee Signature*
              </Form.Label>

              <Col sm={6} className="d-flex flex-column">
                <Form.File className="">
                  <Form.File.Input
                    accept="image/jpeg, image/jpg, image/png"
                    name="employeeSignature"
                    onChange={onFileChange}
                    onClick={e => onFileUploadClick(e)}
                    required
                    ref={fileInputRef}
                  />
                </Form.File>
                {imageErrors && (
                  <p className="text-danger mt-3">{imageErrors}</p>
                )}
                {croppedImage && (
                  <div className="signature mt-2">
                    <Image
                      src={URL.createObjectURL(croppedImage)}
                      alt={
                        employeeDetails.employeeSignature &&
                        employeeDetails.employeeSignature.name
                      }
                      className="img-responsive border rounded border-dark p-1 ml-auto"
                      height="73px"
                      width="269px"
                    />
                  </div>
                )}
              </Col>
            </Form.Group>

            <Modal size="lg" show={showImage} onHide={onImageClose}>
              <Modal.Header closeButton>
                <Modal.Title>Select Signature Area </Modal.Title>
              </Modal.Header>
              <Modal.Body className="crop-container">
                <ReactCrop
                  src={employeeDetails.employeeSignature}
                  onImageLoaded={onLoad}
                  crop={crop}
                  onChange={c => setCrop(c)}
                  onComplete={c => onCompleteCrop(c)}
                  crossorigin="anonymous"
                />
                <div className="d-none">
                  <canvas
                    ref={previewCanvasRef}
                    style={{
                      width: Math.round(crop?.width ?? 0),
                      height: Math.round(crop?.height ?? 0)
                    }}
                  />
                </div>
              </Modal.Body>
              <Modal.Footer>
                {!crop.width && !crop.height && (
                  <p className="text-danger crop-modal-footer">
                    Note : Drag and select Signature Area
                  </p>
                )}
                <Button
                  onClick={showCroppedImage}
                  variant="primary"
                  disabled={!crop.width && !crop.height}
                >
                  Crop
                </Button>
                <Button onClick={onImageClose} variant="secondary">
                  Close
                </Button>
              </Modal.Footer>
            </Modal>
            <Modal size="xl" show={showPdf} onHide={handleClosePdf}>
              <Modal.Header closeButton>
                <Modal.Title>{pdfType} PDF</Modal.Title>
              </Modal.Header>

              <Modal.Body className="align-items-center">
                <PDFViewer className="mt-3" width="100%" height="1000">
                  <PDFTemplate
                    pdfType={pdfType}
                    employeeDetails={employeeDetails}
                  />
                </PDFViewer>
              </Modal.Body>

              <Modal.Footer>
                <Button onClick={handleClosePdf} variant="secondary">
                  Close
                </Button>
              </Modal.Footer>
            </Modal>

            {submitAlert && (
              <Dialog
                header="Confirmation"
                message="You can submit this form only once, so please verify the details before proceeding to submit."
                closeButtonText="CANCEL"
                saveButtonText="PROCEED"
                handleClose={() => setSubmitAlert(false)}
                handleSave={saveEmployeeFormDetails}
              />
            )}

            {errorAlert && (
              <ErrorDialog
                header="Error"
                message={errorMessage}
                closeButtonText="Close"
                handleClose={() => setErrorAlert(false)}
              />
            )}

            <Form.Group as={Row}>
              <Form.Label column sm={4} className="text-right">
                Preview
              </Form.Label>
              <Col sm={6} className="d-flex justify-content-between">
                <Button
                  variant="outline-secondary"
                  onClick={() => generatePdf()}
                  disabled={!isValid}
                >
                  Leaves Encashment PDF
                </Button>

                <Button
                  variant="outline-secondary"
                  onClick={() => generatePdf("Gratuity Declaration")}
                  disabled={!isValid}
                >
                  Gratuity Declaration PDF
                </Button>
              </Col>
            </Form.Group>
            <Form.Group
              as={Row}
              className="justify-content-center mt-4"
              controlId="existingIdImage"
            >
              <Col className="d-flex justify-content-center">
                <Button
                  onClick={() => setSubmitAlert(true)}
                  disabled={!isValid}
                >
                  SUBMIT
                </Button>
              </Col>
            </Form.Group>
          </Card.Body>
          <Card.Footer>
            <b>Notes</b> :
            <ul>
              <li>
                Employee Signature image should be less than <b>2 MB</b> and
                must be of
                <b> jpeg/jpg/png </b>formats.
              </li>
              <li>
                After selecting the signature image, make sure to crop the
                respective signature area.
              </li>
              <li>
                Please make sure to Preview the signature image after cropping
                it.
              </li>
              <li>
                You can preview Leave Enchashment and Gratuity Declaration PDFs
                once you enter all the required fields.
              </li>
            </ul>
          </Card.Footer>
        </Card>
        <PageSpinner show={submitLoading} />
      </div>
    </Form>
  );
};

export default LeaveAndGratuityForm;
